/**
 * Los tres estados de un bloque de datos — cargando, roto y vacío — para los
 * seis módulos.
 *
 * Antes había **un archivo por módulo** y, peor, tres nombres para el mismo
 * componente: `Cargando` en cuatro y `BloqueCargando` en dos, `ErrorDeCarga` /
 * `ErrorDelBloque` / `BloqueError`, y la prop del texto del vacío se llamaba
 * `mensaje` en dos módulos y `titulo` en otro. La duplicación de código ya se
 * había resuelto componiendo `ui/BlockState`, pero la de **vocabulario** no: el
 * mismo hecho se llamaba y se veía distinto según la pantalla. Este archivo es
 * el único lugar donde vive ese vocabulario.
 *
 * Tres divergencias que acá quedan decididas de una sola forma:
 *
 *  - **El título del error es siempre "No pudimos cargar X".** Adquisición decía
 *    "No pudimos **traer**"; una pantalla no habla distinto que las otras porque
 *    la escribió otro.
 *  - **El bloque de carga no va punteado.** El borde punteado significa "acá
 *    falta un dato" en todo el panel (`EmptyBlock`, `KpiDerivado`); un dato que
 *    está en camino no falta. Admin y Adquisición lo usaban, las otras cuatro no.
 *  - **El vacío dice `mensaje`**, no `titulo`: era la misma prop con dos nombres.
 *
 * Lo que sigue siendo de cada módulo se pasa por props: el `testId` con el que
 * sus tests distinguen los estados, el título y la nota de una negativa de
 * permiso, si ofrecer Reintentar, y el icono del vacío — la lupa cuando el vacío
 * es el resultado de un filtro, el de dato ausente cuando es que nadie lo cargó,
 * que son dos hechos distintos.
 */
import type { ReactNode } from 'react'

import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  type IconName,
} from '../../ui'

export interface CargandoProps {
  /** Qué se está trayendo, ej. "el tablero de julio 2026". */
  que: string
  /** Alto mínimo reservado, para que la pantalla no salte al resolver. */
  alto?: 'sm' | 'md' | 'lg'
  testId?: string
}

export function Cargando({ que, alto, testId }: CargandoProps) {
  return (
    <LoadingBlock
      que={que}
      {...(alto === undefined ? {} : { alto })}
      {...(testId === undefined ? {} : { testId })}
    />
  )
}

export interface ErrorDeCargaProps {
  /** Qué no se pudo traer, ej. "el portfolio". Arma el título. */
  que: string
  /** El error tal como llegó: el mensaje mostrado sale de la API, nunca de acá. */
  error: unknown
  /** Reemplaza el título entero, ej. "Sin acceso al módulo Admin". */
  titulo?: string
  /**
   * Sin handler no se ofrece reintentar.
   *
   * Si ofrecerlo ante un 403 tiene sentido lo decide el call site y no este
   * componente: en Admin no (un 403 ahí es "no sos admin", y el botón no cambia
   * el rol de nadie), en Adquisición sí (el rol se resuelve por request y
   * reintentar después de que un admin lo cambie es exactamente lo que hay que
   * hacer). Meter la regla acá adentro pisaba una decisión de pantalla.
   */
  onRetry?: () => void
  reintentando?: boolean
  /** Qué hacer al respecto, cuando hay algo que decir además del error. */
  nota?: ReactNode
  testId?: string
}

export function ErrorDeCarga({
  que,
  error,
  titulo,
  onRetry,
  reintentando,
  nota,
  testId,
}: ErrorDeCargaProps) {
  return (
    <ErrorBlock
      que={que}
      error={error}
      {...(titulo === undefined ? {} : { titulo })}
      {...(nota === undefined ? {} : { nota })}
      {...(testId === undefined ? {} : { testId })}
      {...(onRetry === undefined ? {} : { onRetry })}
      {...(reintentando === undefined ? {} : { reintentando })}
    />
  )
}

export interface VacioProps {
  /** Qué no hay, en una oración. Nunca "sin resultados" pelado. */
  mensaje: string
  /** Por qué está vacío, o qué hacer para que deje de estarlo. */
  detalle?: string
  /** `buscar` cuando el vacío lo produjo un filtro; `null` para no mostrar ninguno. */
  icono?: IconName | null
  /** Borde punteado: el vacío es un dato que falta, no una buena noticia. */
  punteada?: boolean
  testId?: string
  /** La acción que llena el vacío. */
  children?: ReactNode
}

export function Vacio({
  mensaje,
  detalle,
  icono,
  punteada,
  testId,
  children,
}: VacioProps) {
  return (
    <EmptyBlock
      mensaje={mensaje}
      {...(detalle === undefined ? {} : { detalle })}
      {...(icono === undefined ? {} : { icono })}
      {...(punteada === undefined ? {} : { punteada })}
      {...(testId === undefined ? {} : { testId })}
    >
      {children}
    </EmptyBlock>
  )
}
